import React, { useEffect, useRef } from 'react';
import { AppState } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { overlayGuard } from '@/containers/PrivacyGuardContextProvider/overlayguard';
import { PatientStackParamList } from '@/navigations/PatientStack';
import { IS_ANDROID } from '@/utils/platform';

type Props = {
  isAuthenticated: boolean;
  children: React.ReactNode;
};
// RAA-1513 This provider triggers the privacy screen when the app goes to background on Android devices.
// The change event listener does not work currently on Android, so we use focus and blur events instead.
// The useOverlayGuard hook is used to prevent triggering the privacy screen when an overlay (like a modal) is shown.
export const PrivacyGuardProvider = ({ isAuthenticated, children }: Props) => {
  const didEnterPrivacy = useRef(false);
  const navigation =
    useNavigation<StackNavigationProp<PatientStackParamList>>();

  useEffect(() => {
    if (!isAuthenticated || !IS_ANDROID) return;

    const onBlur = () => {
      if (overlayGuard.isActive()) return;

      didEnterPrivacy.current = true;
      navigation.navigate('Privacy');
    };

    const onFocus = () => {
      if (!didEnterPrivacy.current) return;

      didEnterPrivacy.current = false;
      navigation.canGoBack() && navigation.goBack();
    };

    const blurSub = AppState.addEventListener('blur', onBlur);
    const focusSub = AppState.addEventListener('focus', onFocus);

    return () => {
      blurSub.remove();
      focusSub.remove();
    };
  }, [isAuthenticated]);

  return <>{children}</>;
};
